import { createContext, useContext, useEffect, useMemo, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
  createTheme,
  responsiveFontSizes,
} from "@mui/material/styles";

export type ThemeMode = "dark" | "light";

export type ThemeContextType = {
  setTheme: React.Dispatch<React.SetStateAction<ThemeMode>>;
  theme: ThemeMode;
};

export type ThemeProviderProps = React.PropsWithChildren<{
  initialValues: {
    theme: ThemeMode;
  };
}>;

export const THEME_CLASS_NAME = "dark-theme";
export const THEME_STORAGE_KEY = "theme";

export const ThemeContext = createContext<ThemeContextType>({
  setTheme: () => undefined,
  theme: "light",
});

const inputMuiOverrides = {
  styleOverrides: {
    root: {
      "&.Mui-disabled": {
        WebkitTextFillColor: "var(--color-text--readonly)",
        color: "var(--color-text--readonly)",
      },
    },
    input: {
      "&.Mui-disabled": {
        WebkitTextFillColor: "var(--color-text--readonly)",
        color: "var(--color-text--readonly)",
      },
    },
    label: {
      "&.Mui-disabled": {
        WebkitTextFillColor: "var(--color-text--readonly)",
        color: "var(--color-text--readonly)",
      },
    },
  },
};

export const applyThemeClass = (theme: ThemeMode): void => {
  if (typeof document === "undefined") {
    return;
  }

  if (theme === "dark") {
    document.documentElement.classList.add(THEME_CLASS_NAME);
    return;
  }

  document.documentElement.classList.remove(THEME_CLASS_NAME);
};

export const getStoredTheme = (): ThemeMode | null => {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const theme = localStorage.getItem(THEME_STORAGE_KEY);

  if (theme === "dark" || theme === "light") {
    return theme;
  }

  return null;
};

export const setStoredTheme = (theme: ThemeMode): void => {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }

  applyThemeClass(theme);
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialValues,
}) => {
  const [theme, setTheme] = useState<ThemeMode>(initialValues.theme);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const muiTheme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme({
          palette: {
            mode: theme,
            background: {
              default: theme === "light" ? "#FAFAFC" : "#212121",
              paper: theme === "light" ? "#FFFFFF" : "#161616",
            },
            primary: {
              light: "#53acfc",
              main: "#2898FC",
              dark: "#1C6AB0",
              contrastText: "#FFF",
            },
            secondary: {
              light: "#568958",
              main: "#7CC57F",
              dark: "#96d098",
              contrastText: "#FFF",
            },
            info: {
              light: theme === "light" ? "#e9edfb" : "#01284b",
              main: theme === "light" ? "#172D82" : "#bec9f4",
              dark: theme === "light" ? "#101F5B" : "#172D82",
              contrastText: "#FFF",
            },
            warning: {
              main: "#FDD835",
              light: "#FFFF6B",
              dark: "#C6A700",
              contrastText: "#FFF",
            },
            success: {
              main: "#7ED752",
              light: "#589639",
              dark: "#97DF74",
              contrastText: "#FFF",
            },
            error: {
              main: "#F44336",
              light: "#F88078",
              dark: "#E31B0C",
              contrastText: "#FFF",
            },
            text: {
              primary: theme === "light" ? "#6B7083" : "rgba(255,255,255,0.90)",
              secondary:
                theme === "light" ? "#1D2A3D" : "rgba(255,255,255,0.90)",
              disabled:
                theme === "light"
                  ? "rgba(55, 55, 55, 0.2)"
                  : "rgba(255,255,255,0.50)",
            },
            action: {
              active: "rgba(107, 112, 131, 0.85)",
              activatedOpacity: 0.75,
              hover: "rgba(107, 112, 131, 0.08)",
              hoverOpacity: 0.08,
              selected: "rgba(107, 112, 131, 0.08)",
              selectedOpacity: 0.08,
              disabled: "rgba(107, 112, 131, 0.5)",
              disabledBackground: "rgba(107, 112, 131, 0.08)",
              disabledOpacity: 0.5,
              focus: "rgba(107, 112, 131, 0.12)",
              focusOpacity: 0.12,
            },
          },
          shape: {
            borderRadius: 16,
          },
          mixins: {
            toolbar: {
              "minHeight": 56,
              "height": 56,
              "@media (min-width:0px) and (orientation: landscape)": {
                minHeight: 56,
                height: 56,
              },
              "@media (min-width:600px)": {
                minHeight: 64,
                height: 64,
              },
            },
          },
          components: {
            MuiSelect: {
              styleOverrides: {
                outlined: {
                  "&:focus": {
                    borderRadius: "inherit",
                  },
                },
              },
            },
            MuiFormControl: {
              styleOverrides: {
                root: {
                  "minWidth": 208,
                  "maxWidth": "100%",
                  "width": "100%",
                  "&.readonly": {
                    "WebkitTextFillColor": "var(--color-text--readonly)",
                    "color": "var(--color-text--readonly)",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--color-text--readonly)",
                    },
                    "label": {
                      WebkitTextFillColor: "var(--color-text--readonly)",
                      color: "var(--color-text--readonly)",
                    },
                  },
                },
              },
            },
            MuiFormControlLabel: {
              styleOverrides: {
                ...inputMuiOverrides.styleOverrides,
                asterisk: {
                  color: "var(--color-error)",
                },
                root: {
                  ".Mui-error &": {
                    "& .MuiFormControlLabel-label": {
                      color: "var(--color-error)",
                    },
                  },
                },
              },
            },
            MuiOutlinedInput: {
              styleOverrides: {
                root: {
                  "&.Mui-disabled": {
                    "WebkitTextFillColor": "var(--color-text--readonly)",
                    "color": "var(--color-text--readonly)",
                    ".MuiOutlinedInput-notchedOutline": {
                      opacity: 0.15,
                    },
                  },
                },
              },
            },
            MuiInputBase: inputMuiOverrides,
            MuiAutocomplete: inputMuiOverrides,
            MuiInputLabel: inputMuiOverrides,
            MuiFormLabel: {
              styleOverrides: {
                ...inputMuiOverrides.styleOverrides,
                asterisk: {
                  "color": "var(--color-error)",
                  ".MuiFormLabel-filled > &": {
                    color: "inherit",
                  },
                },
                root: {
                  "&.Mui-error": {
                    color: "var(--color-error)",
                  },
                },
              },
            },
            MuiCssBaseline: {
              styleOverrides: {
                ".required-asterisk": {
                  color: "var(--color-error)",
                },
                ".required-asterisk.filled": {
                  color: "inherit",
                },
              },
            },
            MuiTab: {
              styleOverrides: {
                root: {
                  "textTransform": "capitalize",
                  "&:hover": {
                    color: "var(--color-primary)",
                  },
                },
              },
            },
            MuiTabs: {
              styleOverrides: {
                indicator: {
                  height: "4px",
                },
              },
            },
            MuiGrid: {
              styleOverrides: {
                root: {
                  ".MuiDataGrid-cell:focus": { outline: "none" },
                  ".MuiDataGrid-cell:focus-within": { outline: "none" },
                  ".MuiDataGrid-cell:select": { outline: "none" },
                  ".MuiDataGrid-columnHeader:focus": { outline: "none" },
                  ".MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                  ".MuiDataGrid-columnHeader:select": { outline: "none" },
                  ".MuiDataGrid-columnSeparator": { display: "none" },
                  ".MuiDataGrid-footerContainer": { display: "none" },
                },
              },
            },
            MuiButton: {
              defaultProps: {
                disableElevation: true,
                variant: "outlined",
              },
              styleOverrides: {
                outlined: {
                  textTransform: "unset",
                },
                root: {
                  borderRadius: "500px",
                  paddingLeft: "24px",
                  paddingRight: "24px",
                  textTransform: "unset",
                },
              },
            },
          },
          typography: {
            h1: {
              fontSize: 36,
              fontStyle: "normal",
              fontWeight: 600,
              letterSpacing: -1,
              lineHeight: 1.33,
            },
            h2: {
              fontSize: 24,
              fontWeight: 600,
              lineHeight: 1.33,
            },
            h3: {
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.33,
            },
            h4: {
              fontSize: 16,
              fontWeight: 500,
              lineHeight: 1.5,
            },
            h5: {
              fontSize: 24,
              fontWeight: 500,
            },
            h6: {
              fontSize: 20,
              fontWeight: 500,
            },
            body1: {
              fontSize: 16,
            },
            body2: {
              fontSize: 14,
            },
            fontFamily: `
            'Poppins',
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            'Roboto',
            'Oxygen',
            'Ubuntu',
            'Cantarell',
            'Fira Sans',
            'Droid Sans',
            'Helvetica Neue',
            sans-serif`,
            fontSize: 14,
          },
        }),
      ),
    [theme],
  );

  const providerValue = useMemo(
    () => ({
      setTheme,
      theme,
    }),
    [theme],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeContext.Provider value={providerValue}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </ThemeContext.Provider>
    </StyledEngineProvider>
  );
};

export const useThemeContext = (): ThemeContextType => {
  return useContext(ThemeContext);
};
